import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ArrowRight, BadgeCheck, Facebook, HeartHandshake, Instagram, Linkedin, Loader2, Mail, PawPrint, RotateCcw, ShieldCheck, Twitter, Youtube,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { FOOTER, SOON, siteUrl } from "@/lib/sites";
import { SiteLink } from "@/components/MegaNav";

// The same footer on pawbazaar.store, care and community (this file is
// copied into each site). Links come from FOOTER in lib/sites.js.
const SOCIAL = { instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin, x: Twitter };
const TRUST = { "shield-check": ShieldCheck, "badge-check": BadgeCheck, "heart-handshake": HeartHandshake, "rotate-ccw": RotateCcw };

const Apple = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current" aria-hidden="true">
    <path d="M16.37 12.77c-.02-2.02 1.65-3 1.73-3.04-.94-1.38-2.41-1.57-2.93-1.59-1.25-.13-2.44.73-3.07.73-.64 0-1.61-.71-2.65-.69-1.36.02-2.62.79-3.32 2.01-1.42 2.46-.36 6.1 1.02 8.1.67.97 1.47 2.07 2.52 2.03 1.01-.04 1.39-.65 2.61-.65 1.22 0 1.56.65 2.63.63 1.09-.02 1.78-.99 2.44-1.97.77-1.12 1.09-2.21 1.1-2.27-.02-.01-2.11-.81-2.13-3.22zM14.36 6.84c.56-.68.93-1.62.83-2.56-.8.03-1.77.53-2.35 1.21-.52.6-.97 1.56-.85 2.48.9.07 1.81-.46 2.37-1.13z" />
  </svg>
);
const Play = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path d="M4 2.5v19l10.2-9.5z" fill="#34A853" /><path d="M4 2.5l13.6 7.7-3.4 3.3z" fill="#4285F4" />
    <path d="M4 21.5l10.2-9.5 3.4 3.3z" fill="#EA4335" /><path d="M17.6 10.2 21 12l-3.4 1.8-3.4-1.8z" fill="#FBBC04" />
  </svg>
);

export default function PawBazaarFooter({ current }) {
  const [email, setEmail] = useState("");
  // Social and app links from Admin › Settings › Company and contact; unset ones are hidden.
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/site-info").then(({ data }) => setInfo(data)).catch(() => setInfo({})); }, []);
  const socials = FOOTER.socials.filter(([key]) => info?.socials?.[key]).map(([key, label]) => [key, label, info.socials[key]]);
  const apps = [info?.app_store_url, info?.play_store_url];
  const [busy, setBusy] = useState(false);
  const join = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return toast.error("Enter a valid email address");
    setBusy(true);
    try { await api.post("/newsletter", { email: email.trim(), site: current }); setEmail(""); toast.success("You're in! Look out for pet-care tips and offers."); }
    catch { toast.error("Couldn't sign you up just now. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <footer className="mt-24 bg-[#FFF6EC] border-t border-marigold/20 text-stoneWarm" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8">
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <div className="flex items-center gap-3">
              <span className="h-11 w-11 rounded-2xl bg-marigold flex items-center justify-center"><PawPrint className="h-6 w-6 text-white" strokeWidth={2.5} /></span>
              <div>
                <div className="font-heading text-2xl font-bold leading-none">PawBazaar</div>
                <div className="text-xs text-stone-600 mt-1">{FOOTER.tagline}</div>
              </div>
            </div>
            <p className="text-sm text-stone-600 mt-4 max-w-xs leading-relaxed">{FOOTER.intro}</p>
            {socials.length > 0 && <div className="flex gap-2 mt-4" data-testid="footer-social-links">
              {socials.map(([key, label, url]) => {
                const Icon = SOCIAL[key];
                return (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={`PawBazaar on ${label}`} data-testid={`footer-social-${key}`}
                    className="h-9 w-9 rounded-full bg-white border border-marigold/20 flex items-center justify-center text-marigold hover:bg-marigold hover:text-white transition-colors">
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>}
          </div>
          {FOOTER.columns.map((col) => (
            <div key={col.title} data-testid={`footer-col-${col.title.toLowerCase()}`}>
              <div className="font-heading font-semibold mb-3">{col.title}</div>
              <ul className="space-y-2 text-sm text-stone-600">
                {col.items.map((it) => (
                  <li key={it.label}><SiteLink item={it} current={current} className="hover:text-marigold transition-colors">{it.label}</SiteLink></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-6 items-center rounded-3xl bg-white border border-marigold/15 p-5 sm:p-6">
          <form onSubmit={join} className="flex flex-col gap-3" data-testid="footer-newsletter">
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 rounded-xl bg-marigold/15 flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-marigold" /></span>
              <div>
                <div className="font-heading font-semibold">Join the PawBazaar family</div>
                <div className="text-xs text-stone-500">Pet care tips, offers and updates in your inbox.</div>
              </div>
            </div>
            <div className="flex rounded-full border border-stone-200 bg-white overflow-hidden max-w-md">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email address" aria-label="Email address"
                className="flex-1 min-w-0 px-4 py-2.5 text-sm outline-none bg-transparent" data-testid="footer-newsletter-email" />
              <button type="submit" disabled={busy} className="px-5 text-sm font-semibold bg-marigold text-white hover:bg-marigold/90 inline-flex items-center gap-1 disabled:opacity-60" data-testid="footer-newsletter-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Subscribe <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </form>
          {(apps[0] || apps[1]) && <div className="flex flex-wrap gap-3 lg:justify-end" data-testid="footer-apps">
            {apps[0] && <a href={apps[0]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-stoneWarm text-white px-4 py-2 hover:bg-black">
              <Apple /><span className="leading-tight"><span className="block text-[10px]">Download on the</span><span className="block text-base font-semibold">App Store</span></span>
            </a>}
            {apps[1] && <a href={apps[1]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-stoneWarm text-white px-4 py-2 hover:bg-black">
              <Play /><span className="leading-tight"><span className="block text-[10px]">GET IT ON</span><span className="block text-base font-semibold">Google Play</span></span>
            </a>}
          </div>}
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="footer-trust">
          {FOOTER.trust.map(([icon, label]) => {
            const Icon = TRUST[icon];
            return <div key={label} className="flex items-center gap-2 text-sm text-stone-700"><Icon className="h-5 w-5 text-teal shrink-0" />{label}</div>;
          })}
        </div>
      </div>
      <div className="border-t border-marigold/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col md:flex-row md:items-center gap-3 text-xs text-stone-600">
          <div>© {new Date().getFullYear()} PawBazaar. All rights reserved.</div>
          <nav className="flex flex-wrap gap-x-5 gap-y-1 md:ml-auto" aria-label="Policies" data-testid="footer-legal">
            {FOOTER.legal.map((it) => <SiteLink key={it.label} item={it} current={current} className="hover:text-marigold">{it.label}</SiteLink>)}
            {process.env.REACT_APP_GA_ID ? (
              <button type="button" className="hover:text-marigold" data-testid="footer-cookie-settings"
                onClick={() => window.dispatchEvent(new Event("pb:cookie-settings"))}>Cookie settings</button>
            ) : null}
          </nav>
          <div className="md:ml-6 font-medium inline-flex items-center gap-1" aria-label="Country"><Globe className="h-3.5 w-3.5" /> India (IN)</div>
        </div>
      </div>
    </footer>
  );
}

/** /soon/:slug — a page that's planned but not built yet. */
export function ComingSoon() {
  const { slug } = useParams();
  const title = SOON[slug] || "This page";
  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="coming-soon">
      <span className="h-14 w-14 rounded-2xl bg-marigold/15 inline-flex items-center justify-center"><PawPrint className="h-7 w-7 text-marigold" /></span>
      <h1 className="font-heading text-3xl font-semibold mt-5">{title}</h1>
      <p className="text-stone-600 mt-3">We're working on this page. It'll be here soon.</p>
      <a href={siteUrl("shop")} className="mt-6 inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-stoneWarm text-white text-sm font-semibold hover:bg-stoneWarm/90">
        Back to PawBazaar <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  );
}
